// src/hooks/useBpmnPoller.ts

import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiResponseRegenerate, callRegenerateApi, useRFS } from "@/api/rfs";
import { swalErrorDefault } from "@/utils/lib/swal";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 5 * 60 * 1000;

// Tentukan apa yang akan dikembalikan oleh hook ini
interface useRegeneratePollingReturn {
  startPolling: () => void;
  currentStatus: string | null; // <-- BERUBAH: Dari string[] menjadi string | null
  isLoading: boolean;
}

export const useRegeneratePolling = ({
  id,
}: {
  id?: string;
}): useRegeneratePollingReturn => {
  if (!id) {
    console.error("RFS ID is required");
  }

  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Refetch RFS berkala selama generateStatus IN_PROGRESS, berhenti saat
  // selesai atau setelah POLL_MAX_DURATION_MS.
  const pollStartedAt = useRef<number | null>(null);
  const { data } = useRFS(
    { id: id! },
    {
      refetchInterval: (query) => {
        if (query.state.data?.generateStatus !== "IN_PROGRESS") {
          pollStartedAt.current = null;
          return false;
        }
        pollStartedAt.current ??= Date.now();
        return Date.now() - pollStartedAt.current < POLL_MAX_DURATION_MS
          ? POLL_INTERVAL_MS
          : false;
      },
    },
  );

  const invalidateGenerateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["rfsLogs", { id }],
    });
    queryClient.invalidateQueries({
      queryKey: ["opmFdts", { rfsId: id }],
    });
    queryClient.invalidateQueries({
      queryKey: ["generateABDFDTs", { rfsId: id }],
    });
  };

  const mutation = useMutation<ApiResponseRegenerate, Error, string>({
    mutationFn: callRegenerateApi,
    onSuccess: (data) => {
      if (data.data === "IN_PROGRESS") {
        setCurrentStatus("IN PROGRESS");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["rfss", { id }],
      });
      invalidateGenerateQueries();
    },
    onError: () => {
      swalErrorDefault.fire({
        text: "Regenerate failed. Please try again.",
      });
    },
  });

  const statusFromData = data?.generateStatus;

  // Saat polling selesai (IN_PROGRESS -> SUCCESS/FAILED), muat ulang data
  // generate yang tadi di-invalidate ketika proses masih berjalan.
  const prevStatus = useRef(statusFromData);
  useEffect(() => {
    if (
      prevStatus.current === "IN_PROGRESS" &&
      statusFromData !== "IN_PROGRESS"
    ) {
      invalidateGenerateQueries();
    }
    prevStatus.current = statusFromData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFromData]);

  const startPolling = () => {
    if (id) {
      pollStartedAt.current = null;
      mutation.mutate(id);
    }
  };

  // --- RETURN BERUBAH ---
  return {
    startPolling,
    currentStatus: statusFromData ?? currentStatus, // Kembalikan state yang baru
    isLoading: mutation.isPending,
  };
};
